import {
  AddDsoLineItemParams,
  DsoCart,
  DsoCartResponse,
  DsoChildLineItem,
  DsoDiscounts,
  DsoDiscountsResponse,
  DsoLineItem,
  DsoPermissionsResponse,
} from 'types';
import RestUtility from '../../../restUtility';
import {
  dsoBootstrapEndPoint,
  dsoCartEndPoint,
  dsoDiscountsEndPoint,
  dsoPermissionsEndPoint,
} from '../../../restUtility/restEndpoints';
import { notifyError } from 'config/setupBugsnag';

export class DsoCartService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async bootstrapUserAndCart() {
    try {
      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoBootstrapEndPoint}`,
        {}
      );

      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::bootstrapUserAndCart', error);
      throw error;
    }
  }

  async fetchDsoPermissions() {
    try {
      const response = await this.restUtility.get<DsoPermissionsResponse>(
        dsoPermissionsEndPoint
      );

      return response;
    } catch (error) {
      notifyError('ApiError:: DSO::Permissions', error);
      throw error;
    }
  }

  async getCartDetails() {
    try {
      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoCartEndPoint}`,
        {}
      );

      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: getCartDetails', error);
      throw error;
    }
  }

  async reorderPreviousOrder(orderId: number) {
    try {
      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoCartEndPoint}/reorder`,
        { source_order_id: orderId }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: reorderPreviousOrder', error);
      throw error;
    }
  }

  async switchToSingleShip() {
    try {
      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoCartEndPoint}/switch_to_single_ship`,
        {}
      );
      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: switchToSingleShip', error);
      throw error;
    }
  }

  async addLineItemsToCart(lineItems: AddDsoLineItemParams[]) {
    try {
      const payload = {
        line_items: lineItems.map(
          ({ spreeVariantId, quantity, shipAccountNumber }) => ({
            spree_variant_id: parseInt(spreeVariantId),
            quantity,
            ship_account_number: shipAccountNumber,
          })
        ),
      };

      const response = await this.restUtility.post<DsoCartResponse>(
        `${dsoCartEndPoint}/items`,
        payload
      );
      const responseData = this.buildDsoCart(response);
      return responseData;
    } catch (error) {
      notifyError('ApiError:: DSO::addLineItemsToCart', error);
      throw error;
    }
  }

  async updateLineItem(
    childLineItemId: number,
    quantity: number,
    shipAccountNumber: string
  ) {
    try {
      const params = {
        child_line_item_id: childLineItemId,
        quantity: quantity,
        ship_account_number: shipAccountNumber,
      };

      const response = await this.restUtility.put<DsoCartResponse>(
        `${dsoCartEndPoint}/items`,
        params
      );
      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::updateLineItem', error);
      throw error;
    }
  }

  async removeLineItem(childLineItemId: number) {
    try {
      const response = await this.restUtility.delete<DsoCartResponse>(
        `${dsoCartEndPoint}/items`,
        { child_line_item_id: childLineItemId }
      );
      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::removeLineItem', error);
      throw error;
    }
  }

  async dsoEmptyCart() {
    try {
      const response = await this.restUtility.delete<DsoCartResponse>(
        `${dsoCartEndPoint}/empty`
      );
      return this.buildDsoCart(response);
    } catch (error) {
      notifyError('ApiError:: DSO::dsoEmptyCart', error);
      throw error;
    }
  }

  async getDiscounts(): Promise<DsoDiscounts> {
    try {
      const response = await this.restUtility.get<DsoDiscountsResponse>(
        `${dsoDiscountsEndPoint}`
      );
      return this.buildDiscounts(response);
    } catch (error) {
      notifyError('ApiError:: getDiscounts', error);
      throw error;
    }
  }

  buildDsoCart(response: DsoCartResponse): DsoCart {
    if (!response?.data) {
      return {
        orderId: 0,
        orderNumber: '',
        state: '',
        currency: '',
        lineItems: [],
        displayOrderSubTotal: '',
        displayOrderAdjustmentTotal: '',
        displayOrderNetTotal: '',
        displayOrderShippingTotal: '',
        displayOrderTaxTotal: '',
        displayOrderGrandTotal: '',
        displayOrderDiscountTotal: '',
        displayOrderSavingsTotal: '',
        billingAddress: null,
        billAccountNumber: null,
        shippingMethod: '',
        paymentMethod: '',
        isMultiShipOrder: false,
        lineItemSavings: '',
        shipmentSavings: '',
        orderSavings: '',
        itemCount: 0,
        promotions: [],
      };
    }

    const cart: DsoCart = {
      orderId: response.data.order_id,
      orderNumber: response.data.order_number,
      state: response.data.state,
      currency: response.data.currency,
      lineItems: [],
      displayOrderSubTotal: response.data.display_order_sub_total,
      displayOrderAdjustmentTotal: response.data.display_order_adjustment_total,
      displayOrderNetTotal: response.data.display_order_net_total,
      displayOrderShippingTotal: response.data.display_order_shipping_total,
      displayOrderTaxTotal: response.data.display_order_tax_total,
      displayOrderGrandTotal: response.data.display_order_grand_total,
      displayOrderDiscountTotal: response.data.display_order_discount_total,
      displayOrderSavingsTotal: response.data.display_order_savings_total,
      billingAddress: response.data.billing_address?.bill_address || null,
      billAccountNumber:
        response.data.billing_address?.bill_account_number || null,
      shippingMethod: response.data.shipping_method?.label ?? '',
      paymentMethod: response.data.payment_method?.label ?? '',
      isMultiShipOrder: response.data.is_multi_ship_order,
      lineItemSavings: '',
      shipmentSavings: '',
      orderSavings: '',
      itemCount: (response.data.line_items ?? []).length,
      promotions: [],
    };

    const lineItems = response.data.line_items ?? [];
    lineItems.forEach(lineItem => {
      const mainItem: DsoLineItem = {
        lineItemId: lineItem.line_item_id,
        name: lineItem.variant.name,
        sku: lineItem.variant.sku,
        imageUrl: lineItem.variant.image_url ?? '/images/noimage.jpeg',
        quantity: lineItem.quantity,
        displaySubTotal: lineItem.display_sub_total,
        displayAdjustmentTotal: lineItem.display_adjustment_total,
        displayNetTotal: lineItem.display_net_total,
        discountPercentage: lineItem.discount_percentage,
        children: [],
        inStock: lineItem.variant.in_stock,
        slug: lineItem.variant.slug,
        quantityLimit: lineItem.variant.quantity_limit ?? [
          1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
        ],
        displayUnitPrice: lineItem.variant.display_unit_price,
        spreeVariantId: lineItem.variant.id,
      };

      (lineItem.children ?? []).forEach(child => {
        const childLineItem: DsoChildLineItem = {
          childLineItemId: child.child_line_item_id,
          quantity: child.quantity,
          shipAccountNumber: child.ship_account_number,
          displayUnitPrice: child.display_unit_price,
          displaySubTotal: child.display_sub_total,
          displayAdjustmentTotal: child.display_adjustment_total,
          displayNetTotal: child.display_net_total,
          discountPercentage: child.discount_percentage,
          displayDiscountTotal: child.display_discount_total,
        };

        mainItem.children.push(childLineItem);
      });

      cart.lineItems.push(mainItem);
    });

    return cart;
  }

  private buildDiscounts(response: DsoDiscountsResponse): DsoDiscounts {
    const emptyResult: DsoDiscounts = {
      bulkDiscountProducts: [],
      globalDiscounts: [],
    };

    if (!response) {
      return emptyResult;
    }

    if (!response.data || typeof response.data !== 'object') {
      return emptyResult;
    }

    if (
      !response.data.bulk_discount_products ||
      !Array.isArray(response.data.bulk_discount_products)
    ) {
      return emptyResult;
    }

    const result: DsoDiscounts = {
      bulkDiscountProducts: [],
      globalDiscounts: [],
    };

    response.data.bulk_discount_products.forEach(product => {
      const mappedDiscounts = (product.discounts ?? []).map(discount => ({
        title: discount.name,
        description: discount.description,
        displayPriceAfterDiscount:
          discount.display_price_after_discount ?? null,
      }));

      if (product.scope === 'global') {
        result.globalDiscounts.push(...mappedDiscounts);
      } else {
        result.bulkDiscountProducts.push({
          sku: product.sku ?? '',
          discounts: mappedDiscounts,
        });
      }
    });

    return result;
  }
}
